import React, { createContext, useState, useContext, useCallback } from 'react';
import Toast from '../components/Toast/Toast';

const ToastContext = createContext();

// Default duration for toast notifications in milliseconds
const DEFAULT_TOAST_DURATION_MS = 3000;

/** Supported visual variants for toast notifications. */
export const TOAST_VARIANT = Object.freeze({
  ERROR: 'error',
  SUCCESS: 'success',
});

/**
 * ToastProvider component - Provides global toast notification functionality
 * @param {Object} props - Component properties
 * @param {React.ReactNode} props.children - Child components that will have access to the context
 * @returns {JSX.Element} The context provider wrapper component with Toast
 */
export function ToastProvider({ children }) {
  const [toast, setToast] = useState({
    message: '',
    isVisible: false,
    duration: DEFAULT_TOAST_DURATION_MS,
    variant: TOAST_VARIANT.ERROR,
  });

  /**
   * Show a toast notification
   * @param {string} message - The message to display
   * @param {string} variant - Visual toast variant.
   * @param {number} duration - Duration in milliseconds.
   */
  const showToast = useCallback((
    message,
    variant = TOAST_VARIANT.ERROR,
    duration = DEFAULT_TOAST_DURATION_MS
  ) => {
    setToast({
      message,
      isVisible: true,
      duration,
      variant,
    });
  }, []);

  /**
   * Hide the current toast notification
   */
  const hideToast = useCallback(() => {
    setToast(prev => ({
      ...prev,
      isVisible: false
    }));
  }, []);

  return (
    <ToastContext.Provider value={{ showToast, hideToast }}>
      {children}
      <Toast
        message={toast.message}
        isVisible={toast.isVisible}
        onClose={hideToast}
        duration={toast.duration}
        variant={toast.variant}
      />
    </ToastContext.Provider>
  );
}

/**
 * Custom hook to use the toast context
 * @returns {Object} The toast context object containing:
 * - showToast: Function to display a toast notification
 * - hideToast: Function to hide the current toast
 */
export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

export default ToastContext;
